<template>
  <ul id="sidebar-menu">
    <li>
      <!-- A group header toggles; it does not navigate. A parent that does both is ambiguous
           (open me, or go to me?) and worse on touch, where there is no hover to disambiguate.
           The destination lives in an explicit first child instead. -->
      <button
        v-if="children.length"
        type="button"
        class="nav-item nav-item--group"
        :class="{ active: groupActive }"
        :aria-expanded="String(open)"
        @click="open = !open"
      >
        <SidebarIcon :title="title" />
        <span class="nav-text">{{ title }}</span>
        <svg class="nav-chevron" :class="{ 'nav-chevron--open': open }" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <router-link
        v-else
        :to="href"
        class="nav-item"
        :class="{
          active: focus === true || $route.path.includes(href)
        }"
      >
        <SidebarIcon :title="title" />
        <span class="nav-text">
          {{ title }}
        </span>
      </router-link>

      <ul v-if="children.length && open" class="nav-sub">
        <li v-for="child in children" :key="child.href">
          <router-link
            :to="child.href"
            class="nav-subitem"
            :class="{ active: isChildActive(child) }"
          >
            {{ child.title }}
          </router-link>
        </li>
      </ul>
    </li>
  </ul>
</template>

<script>
import SidebarIcon from "@/components/navigation/SidebarIcon.vue";

export default {
  name: "SidebarMenu",

  components: { SidebarIcon },

  props: {
    title: { type: String, default: () => "", required: true },
    // Not required once a group carries children: the header is a toggle, not a destination.
    href: { type: String, default: () => "" },
    focus: { type: Boolean, default: () => false },
    // [{ title, href }] — presence of these turns this item into a collapsible group.
    children: { type: Array, default: () => [] },
  },

  data() {
    // Open on load when the current route is already inside the group, so a deep link does not
    // land the user on a page whose nav entry is hidden.
    return { open: this.routeIsInside() };
  },

  computed: {
    groupActive() {
      return this.focus === true || this.routeIsInside();
    },
  },

  watch: {
    $route() {
      if (this.routeIsInside()) this.open = true;
    },
  },

  methods: {
    routeIsInside() {
      const path = this.$route?.path || "";
      return (this.children || []).some((c) => path === c.href || path.startsWith(c.href + "/"));
    },

    /**
     * Exact match, not startsWith.
     *
     * The children are nested paths (/dashboard/invest, /dashboard/invest/exchange), so a
     * prefix test would light up Overview on every page in the group.
     */
    isChildActive(child) {
      return this.$route?.path === child.href;
    },
  },
};
</script>

<style lang="scss" scoped>
#sidebar-menu {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: #475569;
  text-decoration: none;
  border-radius: 7px;
  margin: 0 8px 4px;
  transition: all 0.2s ease;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;

  &:hover {
    background: #f8fafc;
    color: #0f172a;
  }

  &.active {
    background: rgba(77, 132, 255, 0.1);
    color: #4d84ff;
  }
}

.nav-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-text {
  font-size: 14px;
  font-weight: 400;
}

.nav-item--group {
  width: calc(100% - 16px);
  border: 0;
  background: none;
  cursor: pointer;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.nav-chevron {
  width: 14px;
  height: 14px;
  margin-left: auto;
  transition: transform 0.2s ease;
}

.nav-chevron--open {
  transform: rotate(90deg);
}

.nav-sub {
  list-style: none;
  margin: 0 8px 4px;
  padding: 0;
  /* Aligns the children under the parent's label rather than its icon, so the group reads as
     one block instead of two columns. */
  padding-left: 32px;
}

.nav-subitem {
  display: block;
  padding: 8px 12px;
  border-radius: 6px;
  color: #64748b;
  font-size: 13px;
  text-decoration: none;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;

  &:hover {
    background: #f8fafc;
    color: #0f172a;
  }

  &.active {
    color: #4d84ff;
    font-weight: 500;
  }
}
</style>
